import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, LogOut, User, Menu, X, ChevronDown } from 'lucide-react';
import { isAuthenticated, getAuthData, clearAuthData } from '../../utils/auth';

export const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = isAuthenticated();
  const authData = getAuthData();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const handleAuthNavigation = (path: string) => {
    // If user is on search page, store the current state
    if (location.pathname === '/search') {
      sessionStorage.setItem('returnPath', location.pathname + location.search);
    }
    navigate(path);
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/');
    setIsMenuOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="bg-[#63B56F] shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <Sun className="h-8 w-8 text-white" />
              <span className="text-white text-lg sm:text-xl font-bold">Solar Panel Lookup</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {!isAuth ? (
              <>
                <Link
                  to="/search"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/search') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Search Panels
                </Link>
                <button
                  onClick={() => handleAuthNavigation('/user/login')}
                  className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-white hover:bg-opacity-30 transition-colors"
                >
                  Login
                </button>
                <button
                  onClick={() => handleAuthNavigation('/user/signup')}
                  className="bg-[#193B4E] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#152e3a] transition-colors"
                >
                  Sign Up
                </button>
                
                {/* Dropdown for other login types */}
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center"
                  >
                    Other Logins
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </button>
                  <AnimatePresence>
                    {isDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50"
                        onMouseLeave={() => setIsDropdownOpen(false)}
                      >
                        <div className="py-1">
                          <Link
                            to="/login"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Admin Login
                          </Link>
                          <Link
                            to="/supplier/login"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setIsDropdownOpen(false)}
                          >
                            Supplier Login
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : authData?.user.userType === 'admin' ? (
              <>
                <Link
                  to="/admin"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/admin') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/admin/create"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/admin/create') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Create Panel
                </Link>
                <Link
                  to="/admin/suppliers"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/admin/suppliers') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Suppliers
                </Link>
                <Link
                  to="/admin/supplier-requests"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/admin/supplier-requests') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Requests
                </Link>
                <Link
                  to="/admin/analytics"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/admin/analytics') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Analytics
                </Link>
                <div className="flex items-center space-x-2 text-white">
                  <User className="h-4 w-4" />
                  <span className="text-sm">{authData?.user.full_name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-200 p-2 rounded-md transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : authData?.user.userType === 'supplier' ? (
              <>
                <Link
                  to="/supplier"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/supplier') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Dashboard
                </Link>
                <Link
                  to="/supplier/inventory"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/supplier/inventory') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Inventory
                </Link>
                <div className="flex items-center space-x-2 text-white">
                  <User className="h-4 w-4" />
                  <span className="text-sm">{authData?.user.full_name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-200 p-2 rounded-md transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              // Regular user navigation
              <>
                <Link
                  to="/search"
                  className={`text-white hover:text-gray-200 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive('/search') ? 'bg-white bg-opacity-20' : ''
                  }`}
                >
                  Search Panels
                </Link>
                <div className="flex items-center space-x-2 text-white">
                  <User className="h-4 w-4" />
                  <span className="text-sm">{authData?.user.full_name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-200 p-2 rounded-md transition-colors"
                  title="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-gray-200 p-2 rounded-md transition-colors"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white border-opacity-20"
            >
              <div className="px-2 pt-2 pb-3 space-y-1">
                {!isAuth ? (
                  <>
                    <Link
                      to="/search"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/search') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Search Panels
                    </Link>
                    <button
                      onClick={() => {
                        handleAuthNavigation('/user/login');
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left bg-white bg-opacity-20 text-white hover:bg-opacity-30 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => {
                        handleAuthNavigation('/user/signup');
                        setIsMenuOpen(false);
                      }}
                      className="block w-full text-left bg-[#193B4E] text-white hover:bg-[#152e3a] px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      Sign Up
                    </button>
                    
                    {/* Mobile Other Logins */}
                    <div className="pt-2 border-t border-white border-opacity-20">
                      <p className="text-white text-sm font-medium px-3 py-1">Other Logins</p>
                      <Link
                        to="/login"
                        className="block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-6 py-2 rounded-md text-base transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Admin Login
                      </Link>
                      <Link
                        to="/supplier/login"
                        className="block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-6 py-2 rounded-md text-base transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        Supplier Login
                      </Link>
                    </div>
                  </>
                ) : authData?.user.userType === 'admin' ? (
                  <>
                    <div className="flex items-center space-x-2 text-white px-3 py-2">
                      <User className="h-4 w-4" />
                      <span className="text-base font-medium">{authData?.user.full_name}</span>
                    </div>
                    <Link
                      to="/admin"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/admin') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/admin/create"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/admin/create') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Create Panel
                    </Link>
                    <Link
                      to="/admin/suppliers"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/admin/suppliers') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Suppliers
                    </Link>
                    <Link
                      to="/admin/supplier-requests"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/admin/supplier-requests') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Requests
                    </Link>
                    <Link
                      to="/admin/analytics"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/admin/analytics') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Analytics
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      <LogOut className="h-4 w-4 inline mr-2" />
                      Logout
                    </button>
                  </>
                ) : authData?.user.userType === 'supplier' ? (
                  <>
                    <div className="flex items-center space-x-2 text-white px-3 py-2">
                      <User className="h-4 w-4" />
                      <span className="text-base font-medium">{authData?.user.full_name}</span>
                    </div>
                    <Link
                      to="/supplier"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/supplier') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      to="/supplier/inventory"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/supplier/inventory') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Inventory
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      <LogOut className="h-4 w-4 inline mr-2" />
                      Logout
                    </button>
                  </>
                ) : (
                  // Regular user mobile navigation
                  <>
                    <div className="flex items-center space-x-2 text-white px-3 py-2">
                      <User className="h-4 w-4" />
                      <span className="text-base font-medium">{authData?.user.full_name}</span>
                    </div>
                    <Link
                      to="/search"
                      className={`block text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        isActive('/search') ? 'bg-white bg-opacity-20' : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Search Panels
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left text-white hover:text-gray-200 hover:bg-white hover:bg-opacity-10 px-3 py-2 rounded-md text-base font-medium transition-colors"
                    >
                      <LogOut className="h-4 w-4 inline mr-2" />
                      Logout
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};